import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import VerticalAlignTopRoundedIcon from '@mui/icons-material/VerticalAlignTopRounded';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import FormatSizeRoundedIcon from '@mui/icons-material/FormatSizeRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import PhoneInTalkRoundedIcon from '@mui/icons-material/PhoneInTalkRounded';
import SmsRoundedIcon from '@mui/icons-material/SmsRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TelegramIcon from '@mui/icons-material/Telegram';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

type TermsSection = {
  id: string;
  title: string;
  paragraphs: string[];
};

const accentCards = [
  {
    title: 'الزام پذیرش',
    text: 'ثبت نام، ورود یا خرید در سامانه به معنای پذیرش کامل قوانین جاری مجموعه است.',
  },
  {
    title: 'حفظ مالکیت معنوی',
    text: 'هرگونه انتشار غیرمجاز محتوا یا استفاده از برند بدون مجوز، مشمول پیگیری قانونی خواهد بود.',
  },
  {
    title: 'شرایط مالی',
    text: 'پرداخت ها تابع شرایط اعلامی رسمی هستند و سیاست های مالی طبق قوانین صفحه اعمال می شود.',
  },
];

const SUPPORT_PHONE = '09199236215';
const SUPPORT_PHONE_INTL = '989199236215';
const SYSTEM_FONT_FAMILY = "'Dirooz', 'IranianSans', 'Tahoma', 'Arial', sans-serif";

const supportChannels = [
  {
    label: 'تماس تلفنی',
    href: `tel:${SUPPORT_PHONE}`,
    icon: <PhoneInTalkRoundedIcon fontSize="small" />,
    color: '#0F766E',
    bg: '#F0FDFA',
  },
  {
    label: 'پیامک',
    href: `sms:${SUPPORT_PHONE}`,
    icon: <SmsRoundedIcon fontSize="small" />,
    color: '#1D4ED8',
    bg: '#EFF6FF',
  },
  {
    label: 'واتساپ',
    href: `https://wa.me/${SUPPORT_PHONE_INTL}`,
    icon: <WhatsAppIcon fontSize="small" />,
    color: '#166534',
    bg: '#F0FDF4',
  },
  {
    label: 'تلگرام',
    href: `tg://resolve?phone=${SUPPORT_PHONE_INTL}`,
    icon: <TelegramIcon fontSize="small" />,
    color: '#0369A1',
    bg: '#F0F9FF',
  },
  {
    label: 'بله',
    href: `bale://send?phone=${SUPPORT_PHONE}`,
    icon: <Box component="span" sx={{ fontSize: 12, fontWeight: 900 }}>ب</Box>,
    color: '#1E3A8A',
    bg: '#EEF2FF',
  },
  {
    label: 'ایتا',
    href: `eitaa://chat/${SUPPORT_PHONE}`,
    icon: <Box component="span" sx={{ fontSize: 12, fontWeight: 900 }}>ا</Box>,
    color: '#7C2D12',
    bg: '#FFF7ED',
  },
];

const termsSections: TermsSection[] = [
  {
    id: 'introduction',
    title: 'مقدمه و اعتبار حقوقی',
    paragraphs: [
      'سند حاضر قرارداد خصوصی و لازم الاجرا بین هر شخص مراجعه کننده و موسسه من حقیقی است. فعالیت های موسسه مطابق قوانین جمهوری اسلامی ایران از جمله قانون تجارت الکترونیکی و قانون حمایت از حقوق مصرف کننده انجام می شود.',
      'مدیریت مجموعه من حقیقی حق به روزرسانی این صفحه را دارد و کاربران موظف هستند پیش از استفاده از خدمات، مفاد آخرین نسخه را مطالعه کنند.',
      'استفاده از سامانه، ثبت نام، ورود به سایت، خرید آموزش حضوری، بسته های فیزیکی یا خدمات دیجیتال به منزله آگاهی کامل و پذیرش این قوانین است.',
    ],
  },
  {
    id: 'definitions',
    title: 'الف) تعاریف اصطلاحات',
    paragraphs: [
      'کاربر: هر شخص حقیقی یا حقوقی استفاده کننده از سامانه من حقیقی. با ثبت نام و فشردن دکمه ثبت نام، کاربر تایید می کند مفاد قرارداد را مطالعه کرده است.',
      'خریدار: کاربری که اقدام به ثبت سفارش، خرید و پرداخت هزینه خدمات یا محصولات می کند.',
      'حساب کاربری: حساب اختصاصی کاربر برای مدیریت پروفایل و مشاهده اقلام خریداری شده.',
      'سرویس یا خدمات: هر خدمت غیررایگان ارائه شده در سامانه که منوط به پرداخت هزینه است.',
      'محصول: هر یک از محصولات دیجیتال یا حضوری عرضه شده در فروشگاه و سامانه من حقیقی.',
      'تحویل محصول یا سرویس: ارائه لینک دانلود، اطلاعات دسترسی یا اعطای مجوز بهره برداری مطابق فاکتور یا قرارداد.',
      'کیف پول (اعتبار مالی)، شارژ الکترونیکی، تیکت، سیستم جامع تیکت، پشتیبانی و درگاه پرداخت بر اساس تعاریف درج شده در متن قوانین معتبر و ملاک عمل است.',
    ],
  },
  {
    id: 'license',
    title: 'ب) مجوز استفاده',
    paragraphs: [
      'من حقیقی حق استفاده انحصاری، قابل فسخ و غیرقابل انتقال از سامانه را به کاربر اعطا می کند. تمامی حقوق مالکیت سامانه متعلق به موسسه است.',
      'کاربر صرفا استفاده کننده سامانه است و هیچ رابطه استخدامی، پیمانکاری یا مشارکتی با موسسه ندارد.',
      'مدیر سایت می تواند طبق صلاحدید خود محتوا را موقتا یا دائما حذف یا از دسترس خارج کند و این اقدام حقی برای اعتراض ایجاد نمی کند.',
    ],
  },
  {
    id: 'account',
    title: 'ث) حساب کاربری و تعهدات کاربر',
    paragraphs: [
      'هر کاربر مجاز به ایجاد یک حساب کاربری است. در صورت احراز چند حساب برای یک شخص، مدیر سایت می تواند نسبت به حذف یا مسدودسازی آن ها اقدام کند.',
      'استفاده از خدمات برای اشخاص دارای ۱۸ سال تمام و اهلیت قانونی مجاز است. افراد زیر ۱۸ سال باید از طریق والدین یا قیم قانونی اقدام کنند.',
      'کاربر متعهد است اطلاعات ثبت نامی را دقیق و به روز ارائه کند. مسئولیت اطلاعات نادرست بر عهده کاربر است.',
      'حفظ محرمانگی اطلاعات ورود بر عهده صاحب حساب است و مسئولیت فعالیت های انجام شده تا پیش از اعلام سوءاستفاده، با کاربر خواهد بود.',
      'اشتراک گذاری حساب کاربری یا انتقال آن به دیگران مجاز نیست. استفاده شخصی از محصولات الزامی است و انتشار محتوا پیگرد قانونی دارد.',
    ],
  },
  {
    id: 'intellectual-property',
    title: 'ج و چ) مالکیت معنوی',
    paragraphs: [
      'کلیه محتوا، طراحی، نشان تجاری، لوگو، متن، تصویر، داده ها و نرم افزارهای سامانه متعلق به موسسه من حقیقی است و تحت حمایت قوانین مربوطه قرار دارد.',
      'هرگونه استفاده، بازنشر یا کپی برداری بدون مجوز کتبی ممنوع است و پیگیری قانونی خواهد داشت.',
      'خرید هر محصول صرفا مجوز بهره برداری شخصی، غیرقابل انتقال و مطابق شرایط قرارداد را ایجاد می کند.',
      'فروش یا انتشار غیرمجاز محتوا در سایر پلتفرم ها می تواند مشمول جبران خسارت قراردادی و قانونی باشد.',
    ],
  },
  {
    id: 'payment',
    title: 'د) پرداخت و لغو سفارش',
    paragraphs: [
      'پرداخت ها فقط از طریق مسیرهای اعلامی رسمی معتبر است. واریز به حساب های غیرمجاز خارج از مسئولیت موسسه خواهد بود.',
      'مطابق قوانین جاری مجموعه، پس از خرید، امکان تغییر یا جابه جایی سفارش وجود ندارد مگر در موارد خاص مصرح در قوانین.',
      'درخصوص برخی کارگاه ها، اعلام عدم حضور تنها در بازه زمانی تعیین شده امکان تبدیل اعتبار را فراهم می کند و پس از آن تبدیل انجام نمی شود.',
    ],
  },
  {
    id: 'suspension',
    title: 'ذ) قطع خدمات و مسدودسازی حساب',
    paragraphs: [
      'موسسه در صورت احراز رفتار مغایر منافع سامانه یا کاربران، حق مسدودسازی دسترسی کاربر را دارد.',
      'از جمله مصادیق: انتشار غیرمجاز محصولات، استفاده از هویت غیرواقعی، داشتن چند حساب، توهین یا افترا، تبلیغ پلتفرم های دیگر و ایجاد خسارت اعتباری به مجموعه.',
      'در صورت مسدودسازی، امکان بازیابی دسترسی خدمات یا پشتیبانی برای حساب مذکور ممکن است وجود نداشته باشد.',
    ],
  },
  {
    id: 'security',
    title: 'س) امنیت و محرمانگی',
    paragraphs: [
      'موسسه تلاش خود را برای تامین امنیت پایه سرویس ها به کار می گیرد، اما امنیت مطلق در بستر اینترنت قابل تضمین نیست.',
      'مسئولیت ضعف امنیتی ناشی از ابزارها، دستگاه ها یا سهل انگاری کاربر بر عهده خود کاربر است.',
      'موسسه متعهد به محرمانگی داده های حساب کاربری بوده و از افشای آن به اشخاص ثالث خارج از چهارچوب قانونی خودداری می کند.',
    ],
  },
  {
    id: 'disputes',
    title: 'ش) حل اختلاف و سایر شرایط',
    paragraphs: [
      'در صورت بروز اختلاف، ابتدا مذاکره دوستانه و در صورت عدم توافق، رسیدگی توسط مراجع قضایی صالح تهران انجام می شود.',
      'هرگونه استفاده غیرمجاز از برند، لوگو و محتوای اختصاصی ممنوع و دارای پیگرد قانونی است.',
      'آموزش های ارائه شده در حوزه توسعه فردی هستند و جایگزین مشاوره های تخصصی پزشکی، حقوقی، مالی یا روانشناسی نیستند.',
      'ثبت نام قطعی به منزله پذیرش شرایط مالی، تعهد پرداخت کامل، و عدم امکان فسخ یا استرداد وجه طبق مفاد قرارداد است.',
      'تمامی محصولات صرفا از طریق سایت رسمی `www.manehaghighi.com` عرضه می شوند.',
    ],
  },
];

const Terms: React.FC = () => {
  const [activeSection, setActiveSection] = useState(termsSections[0].id);
  const [readingProgress, setReadingProgress] = useState(0);
  const [query, setQuery] = useState('');
  const [fontScale, setFontScale] = useState<'normal' | 'large'>('normal');
  const [showOnlyMatches, setShowOnlyMatches] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>(termsSections.map((section) => section.id));
  const [copiedSectionId, setCopiedSectionId] = useState<string | null>(null);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);
  const [supportCopied, setSupportCopied] = useState(false);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const isMobile = useMediaQuery('(max-width:899px)');

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const progress = maxScroll > 0 ? Math.min(100, Math.max(0, (scrollTop / maxScroll) * 100)) : 0;
      setReadingProgress(progress);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target?.id) {
          setActiveSection(visible.target.id);
        }
      },
      {
        rootMargin: '-25% 0px -55% 0px',
        threshold: [0.2, 0.4, 0.6, 0.8],
      },
    );

    termsSections.forEach((section) => {
      const node = document.getElementById(section.id);
      if (node) observer.observe(node);
    });

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === '/' && !event.ctrlKey && !event.metaKey && !event.altKey) {
        const target = event.target as HTMLElement | null;
        const isInputLike =
          target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.getAttribute('contenteditable') === 'true';
        if (!isInputLike) {
          event.preventDefault();
          searchInputRef.current?.focus();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const estimatedReadingTime = useMemo(() => {
    const wordCount = termsSections.reduce((count, section) => {
      return count + section.paragraphs.join(' ').split(' ').filter(Boolean).length;
    }, 0);
    return Math.max(3, Math.round(wordCount / 190));
  }, []);

  const normalizedQuery = query.trim().toLowerCase();

  const filteredSections = useMemo(() => {
    if (!normalizedQuery) return termsSections;
    return termsSections.filter((section) => {
      if (section.title.toLowerCase().includes(normalizedQuery)) return true;
      return section.paragraphs.some((paragraph) => paragraph.toLowerCase().includes(normalizedQuery));
    });
  }, [normalizedQuery]);

  const displayedSections = showOnlyMatches && normalizedQuery ? filteredSections : termsSections;
  const matchedSectionIds = useMemo(() => filteredSections.map((section) => section.id), [filteredSections]);

  const totalMatches = useMemo(() => {
    if (!normalizedQuery) return 0;
    return termsSections.reduce((count, section) => {
      const escaped = normalizedQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const titleMatches = section.title.toLowerCase().match(new RegExp(escaped, 'g'))?.length || 0;
      const paragraphMatches = section.paragraphs.reduce(
        (sum, paragraph) => sum + (paragraph.toLowerCase().match(new RegExp(escaped, 'g'))?.length || 0),
        0,
      );
      return count + titleMatches + paragraphMatches;
    }, 0);
  }, [normalizedQuery]);

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => (prev.includes(sectionId) ? prev.filter((id) => id !== sectionId) : [...prev, sectionId]));
  };

  const expandAll = () => setExpandedSections(termsSections.map((section) => section.id));
  const collapseAll = () => setExpandedSections([]);

  const jumpToSection = (sectionId: string) => {
    const node = document.getElementById(sectionId);
    if (!node) return;
    const offset = 96;
    const y = node.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: y, behavior: 'smooth' });
    setActiveSection(sectionId);
  };

  const copySectionContent = async (section: TermsSection) => {
    const sectionContent = `${section.title}\n\n${section.paragraphs.join('\n\n')}`;
    try {
      await navigator.clipboard.writeText(sectionContent);
      setCopiedSectionId(section.id);
      window.setTimeout(() => setCopiedSectionId(null), 1500);
    } catch (_error) {}
  };

  const getHighlightedText = (text: string) => {
    if (!normalizedQuery) return text;
    const escaped = normalizedQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
    return (
      <>
        {parts.map((part, index) =>
          part.toLowerCase() === normalizedQuery ? (
            <mark key={`${part}-${index}`} className="rounded bg-amber-100 px-0.5 text-inherit">
              {part}
            </mark>
          ) : (
            <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
          ),
        )}
      </>
    );
  };

  const articleTextSize = fontScale === 'large' ? 15.5 : 14;

  useEffect(() => {
    setCurrentMatchIndex(0);
  }, [normalizedQuery]);

  const handleJumpToMatch = (direction: 'next' | 'prev') => {
    if (!matchedSectionIds.length) return;
    setCurrentMatchIndex((prev) => {
      const nextIndex =
        direction === 'next'
          ? (prev + 1) % matchedSectionIds.length
          : (prev - 1 + matchedSectionIds.length) % matchedSectionIds.length;
      jumpToSection(matchedSectionIds[nextIndex]);
      return nextIndex;
    });
  };

  const copySupportPhone = async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_PHONE);
      setSupportCopied(true);
      window.setTimeout(() => setSupportCopied(false), 1500);
    } catch (_error) {}
  };

  return (
    <Box
      dir="rtl"
      sx={{
        minHeight: '100vh',
        backgroundColor: '#F8FAFC',
        pb: { xs: 12, md: 8 },
        // do not set overflow-x: hidden on this root — it breaks position:sticky for the desktop TOC in Chrome
        fontFamily: `${SYSTEM_FONT_FAMILY} !important`,
        '& *': { fontFamily: `${SYSTEM_FONT_FAMILY} !important` },
        '@media print': {
          backgroundColor: '#fff',
          pb: 0,
          '& .no-print': { display: 'none !important' },
          '& .print-only': { display: 'block !important' },
          '& .MuiContainer-root': { maxWidth: '100% !important', px: '0 !important', pt: '0 !important' },
          '& .MuiPaper-root': { boxShadow: 'none !important', border: '1px solid #E2E8F0' },
          '& .print-section': {
            breakInside: 'avoid',
            pageBreakInside: 'avoid',
          },
        },
      }}
    >
      <Box className="no-print" sx={{ position: 'fixed', top: 0, right: 0, left: 0, zIndex: 1200, display: { xs: 'none', sm: 'block' } }}>
        <LinearProgress
          variant="determinate"
          value={readingProgress}
          sx={{
            height: 5,
            backgroundColor: 'rgba(15,23,42,0.08)',
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(to right, #1C3B9B, #C11277)',
            },
          }}
        />
      </Box>

      <Container maxWidth="xl" sx={{ pt: { xs: 1.2, sm: 2.2, md: 6 }, px: { xs: 1, sm: 2.5, md: 3 } }}>
        <Box className="print-only" sx={{ display: 'none', mb: 2.5 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 900, color: '#0F172A' }}>قوانین و مقررات من حقیقی</Typography>
          <Typography sx={{ fontSize: 12, color: '#64748B', mt: 0.7 }}>نسخه چاپی - فروردین ۱۴۰۵</Typography>
        </Box>

        <Paper
          component={motion.section}
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          elevation={2}
          sx={{
            p: { xs: 2.2, sm: 2.8, md: 5 },
            borderRadius: { xs: 3, md: 5 },
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: '#fff',
          }}
        >
          <Box sx={{ position: 'absolute', top: -70, left: -70, width: 220, height: 220, borderRadius: '50%', backgroundColor: 'rgba(28,59,155,0.10)', filter: 'blur(48px)' }} />
          <Box sx={{ position: 'absolute', bottom: -90, right: -80, width: 260, height: 260, borderRadius: '50%', backgroundColor: 'rgba(193,18,119,0.10)', filter: 'blur(60px)' }} />

          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={3} sx={{ position: 'relative' }}>
            <Box>
              <Chip label="سند رسمی استفاده از خدمات" sx={{ mb: 1.5, backgroundColor: '#EEF2FF', color: '#1C3B9B', fontWeight: 700 }} />
              <Typography sx={{ color: '#0F172A', fontWeight: 900, fontSize: { xs: 23, sm: 28, md: 34 }, lineHeight: 1.35 }}>قوانین و مقررات من حقیقی</Typography>
              <Typography sx={{ mt: 1.5, color: '#475569', fontSize: articleTextSize, lineHeight: 1.95, maxWidth: 860 }}>
                لطفا پیش از ثبت نام یا خرید، این سند را با دقت مطالعه کنید. ادامه استفاده از خدمات به معنی پذیرش کامل مفاد قرارداد است.
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 2 }}>
                <Chip size="small" label="خوانایی بالا" sx={{ color: '#1C3B9B', backgroundColor: 'rgba(28,59,155,0.12)' }} />
                <Chip size="small" label="ساختار حقوقی" sx={{ color: '#C11277', backgroundColor: 'rgba(193,18,119,0.12)' }} />
                <Chip size="small" label={`مطالعه حدود ${estimatedReadingTime} دقیقه`} sx={{ color: '#B45309', backgroundColor: '#FEF3C7' }} />
              </Stack>
            </Box>

            <Paper elevation={0} sx={{ p: 2, borderRadius: 3, backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', minWidth: { xs: '100%', md: 190 }, alignSelf: 'flex-start' }}>
              <Typography sx={{ fontSize: 13, color: '#64748B' }}>آخرین بروزرسانی</Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#0F172A' }}>فروردین ۱۴۰۵</Typography>
            </Paper>
          </Stack>
        </Paper>

        {isMobile ? (
          <Stack spacing={1.4} sx={{ mt: 1.5 }}>
            <Paper sx={{ p: 1.2, borderRadius: 3 }}>
              <TextField
                inputRef={searchInputRef}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="جستجو در قوانین..."
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: <SearchRoundedIcon sx={{ color: '#94A3B8', ml: 0.5 }} fontSize="small" />,
                  endAdornment: query ? (
                    <IconButton size="small" onClick={() => setQuery('')} sx={{ mr: 0.3 }}>
                      <ClearRoundedIcon fontSize="small" />
                    </IconButton>
                  ) : null,
                  sx: { borderRadius: 2.5, backgroundColor: '#fff' },
                }}
              />
              {normalizedQuery ? (
                <Chip size="small" label={totalMatches > 0 ? `${totalMatches} نتیجه پیدا شد` : 'موردی پیدا نشد'} sx={{ mt: 1, backgroundColor: '#F1F5F9', color: '#475569' }} />
              ) : null}
            </Paper>

            {displayedSections.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 3, alignItems: 'center' }}>
                نتیجه‌ای برای عبارت جستجو پیدا نشد.
              </Alert>
            ) : null}

            {displayedSections.map((section) => (
              <Paper key={`m-${section.id}`} id={section.id} sx={{ borderRadius: 2.5, overflow: 'hidden' }} style={{ scrollMarginTop: '80px' }}>
                <Accordion expanded={expandedSections.includes(section.id)} onChange={() => toggleSection(section.id)} disableGutters sx={{ boxShadow: 'none' }}>
                  <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ px: 1.25, minHeight: 50 }}>
                    <Typography sx={{ fontSize: 15.5, fontWeight: 900, color: '#0F172A', lineHeight: 1.45 }}>
                      {getHighlightedText(section.title)}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, px: 1.25, pb: 1.6 }}>
                    <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
                      <Button size="small" variant="outlined" startIcon={<ContentCopyRoundedIcon fontSize="small" />} onClick={() => copySectionContent(section)} sx={{ textTransform: 'none', borderRadius: 2, borderColor: '#CBD5E1', color: '#475569' }}>
                        کپی بخش
                      </Button>
                    </Stack>
                    <Stack spacing={1.25}>
                      {section.paragraphs.map((paragraph, paragraphIndex) => (
                        <Typography key={`m-${section.id}-${paragraphIndex}`} sx={{ color: '#475569', lineHeight: 1.95, fontSize: articleTextSize }}>
                          {getHighlightedText(paragraph)}
                        </Typography>
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              </Paper>
            ))}

            <Paper
              className="no-print"
              sx={{
                p: 2,
                borderRadius: 3,
                backgroundColor: '#FFFFFF',
                border: '1px solid #E2E8F0',
                boxShadow: '0 8px 22px rgba(15,23,42,0.06)',
              }}
            >
              <Stack spacing={1.5}>
                <Typography sx={{ fontSize: 17, fontWeight: 900, color: '#0F172A' }}>یادآوری مهم</Typography>
                <Typography sx={{ fontSize: 14, color: '#475569', lineHeight: 1.9 }}>
                  این صفحه نسخه ساختاریافته قوانین است. مبنای حقوقی نهایی، متن کامل قرارداد منتشرشده توسط مدیریت موسسه خواهد بود.
                </Typography>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Box sx={{ mt: '6px', width: 8, height: 8, borderRadius: '50%', backgroundColor: '#1C3B9B', flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 13.5, color: '#475569', lineHeight: 1.85 }}>
                    در صورت ابهام، پیش از خرید از طریق پشتیبانی استعلام بگیرید.
                  </Typography>
                </Stack>
                <Stack spacing={1}>
                  <Button component={Link} to="/login?mode=register" endIcon={<OpenInNewRoundedIcon />} sx={{ textTransform: 'none', borderRadius: 2.2, color: '#fff', fontWeight: 800, backgroundColor: '#1C3B9B', '&:hover': { backgroundColor: '#183387' } }}>
                    بازگشت به ثبت نام
                  </Button>
                  <Button onClick={() => setSupportDialogOpen(true)} variant="outlined" sx={{ textTransform: 'none', borderRadius: 2.2, fontWeight: 700, borderColor: '#CBD5E1', color: '#334155' }}>
                    تماس با پشتیبانی
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        ) : (
        <Box
          sx={{
            display: 'grid',
            gap: { xs: 1.5, md: 3 },
            gridTemplateColumns: { xs: '1fr', md: 'minmax(260px, 300px) minmax(0, 1fr)' },
            mt: { xs: 1.5, md: 3 },
            alignItems: 'start',
          }}
        >
          <Paper
            className="no-print"
            sx={{
              p: { xs: 1.3, sm: 2 },
              borderRadius: 4,
              alignSelf: 'flex-start',
              width: '100%',
              position: { md: 'sticky' },
              // نوار پیشرفت ثابت ~۵px + فاصلهٔ امن از بالا
              top: { md: 'calc(12px + env(safe-area-inset-top, 0px))' },
              display: { xs: 'none', md: 'block' },
              maxHeight: { md: 'calc(100dvh - 24px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))' },
              overflowY: { md: 'auto' },
              overflowX: 'hidden',
              zIndex: 2,
              border: '1px solid #E2E8F0',
              boxShadow: '0 12px 32px rgba(15,23,42,0.07)',
            }}
          >
            <Typography sx={{ mb: 1.5, color: '#0F172A', fontWeight: 800, fontSize: 14 }}>فهرست و ابزار</Typography>

            <TextField
              inputRef={searchInputRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="جستجو در قوانین... ( / )"
              fullWidth
              size="small"
              InputProps={{
                startAdornment: <SearchRoundedIcon sx={{ color: '#94A3B8', ml: 0.5 }} fontSize="small" />,
                endAdornment: query ? (
                  <IconButton size="small" onClick={() => setQuery('')} sx={{ mr: 0.3 }}>
                    <ClearRoundedIcon fontSize="small" />
                  </IconButton>
                ) : null,
                sx: { borderRadius: 2.5, backgroundColor: '#fff' },
              }}
            />

            {normalizedQuery ? (
              <Chip
                size="small"
                label={totalMatches > 0 ? `${totalMatches} نتیجه پیدا شد` : 'موردی پیدا نشد'}
                sx={{ mt: 1.2, backgroundColor: '#F1F5F9', color: '#475569' }}
              />
            ) : null}

            {normalizedQuery && matchedSectionIds.length > 0 ? (
              <Stack direction="row" spacing={0.8} sx={{ mt: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleJumpToMatch('prev')}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  قبلی
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleJumpToMatch('next')}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  بعدی
                </Button>
                <Chip
                  size="small"
                  label={`${currentMatchIndex + 1}/${matchedSectionIds.length}`}
                  sx={{ backgroundColor: '#EEF2FF', color: '#1C3B9B' }}
                />
              </Stack>
            ) : null}

            <Stack spacing={0.8} sx={{ mt: 1.5, pr: 0.4 }}>
              {displayedSections.map((section) => (
                <Button
                  key={section.id}
                  onClick={() => jumpToSection(section.id)}
                  fullWidth
                  sx={{
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    borderRadius: 2.5,
                    px: 1.2,
                    py: 1,
                    fontWeight: activeSection === section.id ? 800 : 600,
                    color: activeSection === section.id ? '#1C3B9B' : '#475569',
                    backgroundColor: activeSection === section.id ? 'rgba(28,59,155,0.10)' : 'transparent',
                    '&:hover': { backgroundColor: '#F8FAFC' },
                  }}
                >
                  <Box component="span" sx={{ position: 'relative', zIndex: 1 }}>
                    {getHighlightedText(section.title)}
                  </Box>
                </Button>
              ))}
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
              <Button size="small" variant="outlined" onClick={expandAll} sx={{ textTransform: 'none', borderRadius: 2 }}>بازکردن همه</Button>
              <Button size="small" variant="outlined" onClick={collapseAll} sx={{ textTransform: 'none', borderRadius: 2 }}>بستن همه</Button>
              <Button
                size="small"
                variant={showOnlyMatches ? 'contained' : 'outlined'}
                onClick={() => setShowOnlyMatches((prev) => !prev)}
                sx={{ textTransform: 'none', borderRadius: 2, backgroundColor: showOnlyMatches ? '#1C3B9B' : undefined }}
              >
                فقط نتایج
              </Button>
            </Stack>

            <Box sx={{ mt: 2.2 }}>
              <Typography sx={{ fontSize: 12, color: '#64748B', mb: 0.6 }}>پیشرفت مطالعه ({Math.round(readingProgress)}٪)</Typography>
              <LinearProgress
                variant="determinate"
                value={readingProgress}
                sx={{
                  height: 7,
                  borderRadius: 999,
                  backgroundColor: '#E2E8F0',
                  '& .MuiLinearProgress-bar': { background: 'linear-gradient(to right, #1C3B9B, #C11277)' },
                }}
              />
            </Box>
          </Paper>

          <Stack spacing={2}>
            <Paper className="no-print" sx={{ p: 1, borderRadius: 3, display: { xs: 'block', md: 'none' } }}>
              <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 0.2 }}>
                {termsSections.map((section) => (
                  <Button
                    key={`mobile-${section.id}`}
                    size="small"
                    onClick={() => jumpToSection(section.id)}
                    sx={{
                      flexShrink: 0,
                      textTransform: 'none',
                      borderRadius: 999,
                      px: 1.5,
                      whiteSpace: 'nowrap',
                      fontSize: 12,
                      minWidth: 'max-content',
                      backgroundColor: activeSection === section.id ? 'rgba(28,59,155,0.10)' : '#F8FAFC',
                      color: activeSection === section.id ? '#1C3B9B' : '#475569',
                    }}
                  >
                    {section.title}
                  </Button>
                ))}
              </Stack>
            </Paper>

            <Box sx={{ display: { xs: 'none', sm: 'grid' }, gap: 1.5, gridTemplateColumns: { sm: 'repeat(3, 1fr)' } }}>
              {accentCards.map((card, index) => (
                <Card key={card.title} component={motion.div} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: index * 0.06 }} sx={{ borderRadius: 3 }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 900, color: '#0F172A' }}>{card.title}</Typography>
                    <Typography sx={{ mt: 1, fontSize: 13, lineHeight: 1.9, color: '#475569' }}>{card.text}</Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>

            <Paper sx={{ p: { xs: 1.1, sm: 1.5 }, borderRadius: 3, display: { xs: 'none', sm: 'block' } }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} flexWrap="wrap" useFlexGap>
                <Chip size="small" label="ابزار مطالعه" sx={{ backgroundColor: '#F1F5F9', color: '#475569' }} />
                <Button startIcon={<FormatSizeRoundedIcon />} size="small" onClick={() => setFontScale('normal')} variant={fontScale === 'normal' ? 'contained' : 'outlined'} sx={{ textTransform: 'none', borderRadius: 2, backgroundColor: fontScale === 'normal' ? '#1C3B9B' : undefined }}>عادی</Button>
                <Button startIcon={<FormatSizeRoundedIcon />} size="small" onClick={() => setFontScale('large')} variant={fontScale === 'large' ? 'contained' : 'outlined'} sx={{ textTransform: 'none', borderRadius: 2, backgroundColor: fontScale === 'large' ? '#1C3B9B' : undefined }}>درشت</Button>
                <Button startIcon={<PrintRoundedIcon />} size="small" onClick={() => window.print()} variant="outlined" sx={{ textTransform: 'none', borderRadius: 2 }}>چاپ / PDF</Button>
              </Stack>
            </Paper>

            {displayedSections.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 3, alignItems: 'center' }}>
                نتیجه‌ای برای عبارت جستجو پیدا نشد. عبارت دیگری امتحان کنید یا فیلتر «فقط نتایج» را غیرفعال کنید.
              </Alert>
            ) : null}

            {displayedSections.map((section, index) => (
              <Paper
                key={section.id}
                id={section.id}
                component={motion.div}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.32, delay: Math.min(index * 0.03, 0.18) }}
                sx={{ borderRadius: { xs: 2.2, md: 4 }, overflow: 'hidden' }}
                className="print-section"
                style={{ scrollMarginTop: '104px' }}
              >
                <Accordion
                  expanded={expandedSections.includes(section.id)}
                  onChange={() => toggleSection(section.id)}
                  disableGutters
                  TransitionProps={{ timeout: 320 }}
                  sx={{
                    boxShadow: 'none',
                    '& .MuiAccordionSummary-root': { transition: 'background-color 220ms ease' },
                    '& .MuiAccordionSummary-expandIconWrapper': { transition: 'transform 280ms cubic-bezier(0.22,1,0.36,1)' },
                    '& .MuiAccordionDetails-root': { transition: 'opacity 240ms ease' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreRoundedIcon />}
                    sx={{
                      px: { xs: 1.2, md: 2.5 },
                      minHeight: { xs: 50, md: 56 },
                      '& .MuiAccordionSummary-content': { my: { xs: 0.8, md: 1.2 } },
                    }}
                  >
                    <Typography sx={{ fontSize: { xs: 15.5, md: 20 }, fontWeight: 900, color: '#0F172A', lineHeight: 1.45 }}>
                      {getHighlightedText(section.title)}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, px: { xs: 1.25, md: 2.8 }, pb: { xs: 1.6, md: 2.6 } }}>
                    <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
                      <Tooltip title={copiedSectionId === section.id ? 'کپی شد' : 'کپی عنوان و متن بخش'}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ContentCopyRoundedIcon fontSize="small" />}
                          onClick={() => copySectionContent(section)}
                          sx={{ textTransform: 'none', borderRadius: 2, borderColor: '#CBD5E1', color: '#475569' }}
                        >
                          کپی بخش
                        </Button>
                      </Tooltip>
                    </Stack>
                    <Stack spacing={1.4}>
                      {section.paragraphs.map((paragraph, paragraphIndex) => (
                        <Typography key={`${section.id}-${paragraphIndex}`} sx={{ color: '#475569', lineHeight: 2, fontSize: articleTextSize }}>
                          {getHighlightedText(paragraph)}
                        </Typography>
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              </Paper>
            ))}

            <Paper
              className="no-print"
              sx={{
                p: { xs: 2.2, md: 2.8 },
                borderRadius: 4,
                backgroundColor: '#FFFFFF',
                border: '1px solid #E2E8F0',
                boxShadow: '0 10px 28px rgba(15,23,42,0.06)',
              }}
            >
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                  <Box>
                    <Typography sx={{ fontSize: { xs: 18, md: 20 }, fontWeight: 900, color: '#0F172A' }}>یادآوری مهم</Typography>
                    <Typography sx={{ mt: 0.6, fontSize: 12.5, color: '#64748B' }}>راهنمای نهایی پیش از ثبت نام و استفاده از خدمات</Typography>
                  </Box>
                  <Chip
                    size="small"
                    label="مبنای نهایی: متن کامل قرارداد"
                    sx={{ fontWeight: 700, backgroundColor: '#EEF2FF', color: '#1C3B9B' }}
                  />
                </Stack>

                <Typography sx={{ fontSize: articleTextSize, color: '#475569', lineHeight: 1.95 }}>
                  این صفحه نسخه ساختاریافته قوانین است. مبنای حقوقی نهایی، متن کامل قرارداد منتشرشده توسط مدیریت موسسه خواهد بود.
                </Typography>

                <Stack spacing={1.1}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Box sx={{ mt: '6px', width: 8, height: 8, borderRadius: '50%', backgroundColor: '#1C3B9B', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 14, color: '#475569', lineHeight: 1.85 }}>
                      استفاده از خدمات به منزله پذیرش شرایط اعلام شده در همین صفحه است.
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Box sx={{ mt: '6px', width: 8, height: 8, borderRadius: '50%', backgroundColor: '#1C3B9B', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 14, color: '#475569', lineHeight: 1.85 }}>
                      در صورت ابهام، پیش از خرید از طریق پشتیبانی استعلام بگیرید.
                    </Typography>
                  </Stack>
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ pt: 0.5 }}>
                  <Button
                    component={Link}
                    to="/login?mode=register"
                    endIcon={<OpenInNewRoundedIcon />}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 2.5,
                      color: '#fff',
                      fontWeight: 800,
                      backgroundColor: '#1C3B9B',
                      px: 2.2,
                      '&:hover': { backgroundColor: '#183387' },
                    }}
                  >
                    بازگشت به ثبت نام
                  </Button>
                  <Button
                    onClick={() => setSupportDialogOpen(true)}
                    variant="outlined"
                    sx={{
                      textTransform: 'none',
                      borderRadius: 2.5,
                      fontWeight: 700,
                      borderColor: '#CBD5E1',
                      color: '#334155',
                      '&:hover': { borderColor: '#94A3B8', backgroundColor: '#F8FAFC' },
                    }}
                  >
                    تماس با پشتیبانی
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        </Box>
        )}
      </Container>

      <Dialog
        open={supportDialogOpen}
        onClose={() => setSupportDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            fontFamily: `${SYSTEM_FONT_FAMILY} !important`,
            '& *': { fontFamily: `${SYSTEM_FONT_FAMILY} !important` },
            mx: { xs: 1.2, sm: 2 },
            width: { xs: 'calc(100% - 16px)', sm: 'auto' },
          },
        }}
        sx={{
          '& .MuiDialogTitle-root, & .MuiDialogContent-root, & .MuiDialogActions-root, & .MuiButton-root': {
            fontFamily: `${SYSTEM_FONT_FAMILY} !important`,
          },
        }}
      >
        <Box sx={{ px: 2.2, pt: 2.2, pb: 1.3, backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2.5,
                display: 'grid',
                placeItems: 'center',
                backgroundColor: '#EEF2FF',
                color: '#1C3B9B',
              }}
            >
              <SupportAgentRoundedIcon />
            </Box>
            <Box>
              <DialogTitle sx={{ p: 0, fontWeight: 900, color: '#0F172A', lineHeight: 1.3 }}>ارتباط با پشتیبانی</DialogTitle>
              <Typography sx={{ fontSize: 12, color: '#64748B' }}>پاسخگویی از طریق تماس و پیام رسان ها</Typography>
            </Box>
          </Stack>
        </Box>

        <DialogContent sx={{ pt: '14px !important', px: { xs: 1.6, sm: 2.2 } }}>
          <Typography sx={{ fontSize: 13.5, color: '#475569', mb: 1.5 }}>
            از طریق تماس تلفنی یا پیام رسان ها با شماره زیر در ارتباط باشید:
          </Typography>
          <Paper sx={{ p: 1.2, borderRadius: 2.5, backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', mb: 1.5 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 900, color: '#0F172A', textAlign: 'center', letterSpacing: '0.3px' }}>
              {SUPPORT_PHONE}
            </Typography>
          </Paper>

          <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' } }}>
            {supportChannels.map((channel) => (
              <Button
                key={channel.label}
                component="a"
                href={channel.href}
                target="_blank"
                rel="noopener noreferrer"
                variant="text"
                sx={{
                  textTransform: 'none',
                  borderRadius: 2.3,
                  fontWeight: 700,
                  border: '1px solid #E2E8F0',
                  color: '#334155',
                  justifyContent: 'flex-start',
                  px: 1.1,
                  py: 1.05,
                  backgroundColor: '#fff',
                  '&:hover': { borderColor: '#CBD5E1', backgroundColor: '#F8FAFC' },
                }}
              >
                <Stack direction="row" spacing={0.9} alignItems="center" sx={{ width: '100%' }}>
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      borderRadius: 1.8,
                      display: 'grid',
                      placeItems: 'center',
                      color: channel.color,
                      backgroundColor: channel.bg,
                    }}
                  >
                    {channel.icon}
                  </Box>
                  <Typography sx={{ fontSize: 13.2, fontWeight: 700, lineHeight: 1.35 }}>{channel.label}</Typography>
                  <Box sx={{ marginInlineStart: 'auto', display: 'grid', placeItems: 'center' }}>
                    <OpenInNewRoundedIcon sx={{ fontSize: 16, color: '#94A3B8' }} />
                  </Box>
                </Stack>
              </Button>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.2, pb: 1.8, justifyContent: 'space-between' }}>
          <Button onClick={copySupportPhone} variant="contained" startIcon={<ContentCopyRoundedIcon fontSize="small" />} sx={{ textTransform: 'none', borderRadius: 2.2, backgroundColor: '#1C3B9B', '&:hover': { backgroundColor: '#183387' } }}>
            {supportCopied ? 'شماره کپی شد' : 'کپی شماره'}
          </Button>
          <Button onClick={() => setSupportDialogOpen(false)} sx={{ textTransform: 'none', borderRadius: 2.2, color: '#475569' }}>
            بستن
          </Button>
        </DialogActions>
      </Dialog>

      {readingProgress > 22 ? (
        <Tooltip title="بازگشت به بالا" className="no-print">
          <IconButton
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            sx={{
              position: 'fixed',
              bottom: 20,
              left: 20,
              zIndex: 1000,
              backgroundColor: '#1C3B9B',
              color: '#fff',
              boxShadow: '0 8px 24px rgba(28,59,155,0.3)',
              '&:hover': { backgroundColor: '#183387' },
            }}
          >
            <VerticalAlignTopRoundedIcon />
          </IconButton>
        </Tooltip>
      ) : null}

      <Paper
        className="no-print"
        elevation={8}
        sx={{
          position: 'fixed',
          right: 10,
          left: 10,
          bottom: 10,
          zIndex: 1300,
          borderRadius: 999,
          p: 0.8,
          display: 'none',
          backgroundColor: 'rgba(255,255,255,0.96)',
          backdropFilter: 'blur(8px)',
        }}
      >
        <Stack direction="row" spacing={0.8} justifyContent="space-between">
          <Button size="small" startIcon={<SearchRoundedIcon />} onClick={() => searchInputRef.current?.focus()} sx={{ textTransform: 'none', borderRadius: 999 }}>
            جستجو
          </Button>
          <Button size="small" startIcon={<PrintRoundedIcon />} onClick={() => window.print()} sx={{ textTransform: 'none', borderRadius: 999 }}>
            PDF
          </Button>
          <Button size="small" startIcon={<VerticalAlignTopRoundedIcon />} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} sx={{ textTransform: 'none', borderRadius: 999 }}>
            بالا
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default Terms;
